import React, { useEffect, useState } from 'react';
import LeadsService from '../../services/LeadsService';
import { toast, ToastContainer } from 'react-toastify';
import UsersService from '../../services/UsersService';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { Modal, Button, Form } from 'react-bootstrap';
import ProductService from '../../services/ProductService';
import { leadStatusOptions, leadSourceOptions, empoyeeCountOptions } from '../../utils/OptionUtils';
import { Country, State, City } from 'country-state-city';
import SelectTag from '../../components/SelectTag';

const CreateLead = ({ show, handleClose, refreshData }) => {
  const { register, handleSubmit, reset, control, formState: { errors }, setError, setValue } = useForm();
  const [AssigneeList, setAssigneeList] = useState([]);
  const [ProductList, setProductList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  useEffect(() => {
    const fetchAssignees = async () => {
      try {
        const response = await UsersService.list();
        const assignees = response.data.data;
        const formattedAssigneeList = assignees.map((assignee) => ({
          value: assignee.id,
          label: assignee.name + '-' + assignee.email,
        }));

        setAssigneeList(formattedAssigneeList);
      } catch (err) {
        toast.error('Failed to load assignees');
      }
    };

    fetchAssignees();
    fetchProducts();
    fetchCountries();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await ProductService.list();
      if (response.status === 200) {
        const productData = response?.data?.data;
        const options = productData.map((product) => ({
          id: product.id,
          name: product.name,
        }));
        setProductList(options);
      }
    } catch (error) {
      console.log(error)
    }
  }

  const onSubmit = async (data) => {
    const requestBody = {
      data: {
        ...data,
        assigned_to: data.assigned_to.value || null,
      }
    };

    try {
      await LeadsService.create(requestBody);
      toast.success('Lead created successfully!');
      reset();
      refreshData();
      handleClose();
    } catch (error) {
      if (error.errors) {
        error.errors.forEach((fieldError) => {
          setError(fieldError.field, {
            type: 'manual',
            message: fieldError.message.isNotEmpty
          })
        });
      } else {
        console.log('Error in creating new lead:', [error]);
        toast.error('An error occurred while creating the lead.');
      }
    }
  };

  // Function to only allow numbers
  const handleInputChange = (e, maxLength) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, maxLength);
  };

  const fetchCountries = () => {
    const countries = Country.getAllCountries().map((country) => ({
      value: country.isoCode,
      label: country.name,
    }));
    setCountryList(countries);
  };
  const handleCountryChange = (selectedCountry) => {
    if (selectedCountry) {
      setSelectedCountry(selectedCountry);
      const states = State.getStatesOfCountry(selectedCountry.value).map(state => ({
        value: state.isoCode,
        label: state.name
      }));
      setStateList(states);
      setValue("state", null);
    } else {
      setStateList([]);
      setValue("state", null);
    }
  };

  const handleStateChange = (selectedState) => {
    if (selectedState && selectedCountry) {
      const cities = City.getCitiesOfState(selectedCountry.value, selectedState.value).map(city => ({
        value: city.name,
        label: city.name
      }));
      setCityList(cities);
      setValue("city", null);
    } else {
      setCityList([]);
      setValue("city", null);
    }
  };

  return (
    <>
      <ToastContainer />
      <Modal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create Lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="row mb-4">
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Control as="select" {...register('status', { required: "The status must be open, converted, closed or closed." })}>
                    <option value="">Select Status</option>
                    {leadStatusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Control>
                  {errors?.status && <div className="invalid-feedback">{errors?.status?.message}</div>}
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Source</Form.Label>
                  <Form.Control as="select" {...register('source', { required: "The status field is required." })}>
                    <option value="">Select Source</option>
                    {leadSourceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Control>
                  {errors?.source && <div className="invalid-feedback">{errors?.source?.message}</div>}
                </Form.Group>
              </div>

              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Assigned To</Form.Label>
                  <Controller
                    name="assigned_to"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={AssigneeList}
                        closeMenuOnSelect={true}
                        isClearable={true}
                      />
                    )}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              
            <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Company</Form.Label>
                  <Form.Control type="text" {...register('company')} />
                </Form.Group>
              </div>
              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label>Email address</Form.Label>
                  <Form.Control
                    type="email"
                    {...register('email', {
                      required: "The email field is required.",
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Email must be a valid email address"
                      }
                    })}
                    className={`form-control ${errors?.email ? 'is-invalid' : ''}`}
                  />
                  {errors?.email && <div className="invalid-feedback">{errors?.email?.message}</div>}
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    maxLength={10}
                    {...register('phone', {
                      required: 'The phone number field is required.',
                      minLength: {
                        value: 10,
                        message: "The phone number must be 10 digits number"
                      },
                      maxLength: {
                        value: 10,
                        message: "The phone number must be 10 digits number"
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Phone number must contain only digits"
                      }
                    })}
                    onInput={(e) => handleInputChange(e, 10)}
                    className={`form-control ${errors?.phone ? 'is-invalid' : ''}`}
                  />
                  {errors?.phone && <div className="invalid-feedback">{errors?.phone?.message}</div>}
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>No. of employees</Form.Label>
                  <SelectTag
                    name="employee_count"
                    control={control}
                    label="Employee Count"
                    options={empoyeeCountOptions}
                    isRequired={true}
                  />
                </Form.Group>
                {errors?.employee_count && <div className="invalid-feedback">{errors?.employee_count?.message}</div>}
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Address</Form.Label>
                  <Form.Control type="text" {...register('address')} />
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Country</Form.Label>
                  <SelectTag
                    name="country"
                    control={control}
                    label="country"
                    options={countryList}
                    onChange={(selectedCountry) => {
                      handleCountryChange(selectedCountry);
                      setValue("state", null);
                      setValue("city", null);
                    }}
                  />
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>State</Form.Label>
                  <SelectTag
                    name="state"
                    control={control}
                    label="state"
                    options={stateList}
                    onChange={(selectedState) => {
                      handleStateChange(selectedState); // Fetch cities based on selected state
                      setValue("city", null);
                    }}
                  />
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <SelectTag
                    name="city"
                    control={control}
                    label="city"
                    options={cityList}
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label>Zip Code</Form.Label>
                  <Form.Control
                    type="text"
                    maxLength={6}
                    {...register('zipcode', {
                      required: "The zipcode field is required.",
                      minLength: {
                        value: 6,
                        message: "Zipcode must be longer than or equal to 6 numbers"
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: ""
                      }
                    })}
                    onInput={(e) => handleInputChange(e, 6)}
                    className={`form-control ${errors?.zipcode ? 'is-invalid' : ''}`}
                  />
                  {errors?.zipcode && <div className="invalid-feedback">{errors?.zipcode?.message}</div>}
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label>No. of users/Licence</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('value', {
                      required: "The no of users field is required.",
                      maxLength: {
                        value: 20,
                        message: "The no of users must be less than or equal to 20 characters"
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Only numeric values are allowed"
                      }
                    })}
                    className={`form-control ${errors?.value ? 'is-invalid' : ''}`}
                  />
                  {errors?.value && <div className="invalid-feedback">{errors?.value?.message}</div>}
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    {...register('name', {
                      required: "The name field is required.",
                      minLength: {
                        value: 2,
                        message: "Name must be longer than or equal to 2 characters"
                      }
                    })}
                    className={`form-control ${errors?.name ? 'is-invalid' : ''}`}
                  />
                  {errors?.name && <div className="invalid-feedback">{errors?.name?.message}</div>}
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Designation</Form.Label>
                  <Form.Control type="text" {...register('designation')} />
                </Form.Group>
              </div>

              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Department</Form.Label>
                  <Form.Control type="text" {...register('department')} />
                </Form.Group>
              </div>

              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Website</Form.Label>
                  <Form.Control type="text" {...register('website')} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Tags</Form.Label>
                  <Form.Control type="text" {...register('tags')} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Product</Form.Label>
                  <Controller
                    name="product_id"
                    rules={{ required: true }}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={ProductList}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        value={ProductList.find(option => option.id === field.value) || null}
                        onChange={(option) => field.onChange(option?.id || '')}
                        isClearable={true}
                      />
                    )}
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" size="sm" onClick={handleClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSubmit(onSubmit)}>Create</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CreateLead;
